import React, {
  Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';

import { AlertService } from '@services/alert';
import Spinner from '@common/components/spinner';
import Icon from '@common/components/icon';
import { Button } from '@common/components/button';
import PostMessageForm, { PostMessageFormData } from '@modules/chat/forms/post-message';
import ConversationsByDay from '@modules/chat/components/conversations-by-day';
import type { Pagination } from '@common/components/list';

import { fetchMessages } from '@modules/chat/api/fetch-messages';
import { deleteMessage } from '@modules/chat/api/delete-message';
import { setMessageEmoji } from '@modules/chat/api/set-message-emoji';
import { postMessage } from '@modules/chat/api/post-message';
import { getAllowedEmojis } from '@modules/chat/api/get-allowed-emojis';

import { useAppSelector } from '@common/hooks/redux/use-app-selector';
import { usePusherMessagesListener } from '@modules/chat/hooks/use-pusher-messages-listener';

import { formatMessages, parseMessages } from '@modules/chat/utils/messages';
import { getCurrentOrgId } from '@modules/organisation/selectors/organisation';
import { selected } from '@modules/core/selectors/logged-user';

import {
  EConversationFilterTypes, type FullConversation, type PrivateMessage, ESearhFilterTypes,
} from '@modules/chat/types/objects';
import type { AllowedEmoji, User, UserStatus } from '@common/types/objects';
import { putMessage } from '@modules/chat/api/put-message';
import { useChatStore } from '@modules/chat/store/chatStore';
import { EConversationFilters, EMessageTypes, RouteParams } from '@modules/chat/definitions';
import { uniqueId } from 'lodash';

interface MessagesProps {
  conversation: FullConversation;
  messages: PrivateMessage[] | null;
  usersStatus: Record<string, UserStatus | null>;
  onNewMessage: (id: string, message: PrivateMessage) => void;
  setMessages: Dispatch<SetStateAction<PrivateMessage[] | null>>;
  onUpdateLastMessage: (
    id: string, propName: keyof PrivateMessage, value: PrivateMessage[keyof PrivateMessage], messageId: string,
  ) => void;
  onEditParticipants: (
    conversationId: string, activities: PrivateMessage | PrivateMessage[], participants: User | User[],
  ) => void;
  basePath: string;
}

const Messages = ({
  conversation, usersStatus, messages,
  onNewMessage, setMessages, onEditParticipants, onUpdateLastMessage,
  basePath,
}: MessagesProps) => {
  const { t } = useTranslation();
  const history = useHistory();

  const listRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { filter: currentFilter } = useParams<RouteParams>();

  const orgId = useAppSelector(getCurrentOrgId);
  const { messageId: messageIdParam } = useParams<{ messageId: string }>();
  const {
    selectedFilter, conversations, handleRetryMessage, searchFilter, search,
    setCurrentConversation, setIsSearchFocused,
    currentConversation, setConversations
  } = useChatStore();

  const showGoToConversation = conversation?.is_message_search && conversation.is_message_search;
  const isSearchMode = searchFilter === ESearhFilterTypes.MESSAGES;

  const [replyToMessage, setReplyToMessage] = useState<PrivateMessage | undefined>(undefined);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [allowedEmojis, setAllowedEmojis] = useState<AllowedEmoji[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isFetchingMore, setIsFetchingMore] = useState(false);

  // Ref to avoid multiple scrolling
  const hasScrolledToHighlight = useRef(false);

  const users = useMemo(() => conversation.participants.reduce((acc, participant) => ({
    ...acc,
    [participant.id]: participant,
  }), {}), [conversation.participants]);

  const loggedUser = useAppSelector(selected);

  const scrollToBottom = useCallback(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [listRef]);

  useEffect(() => {
    const load = async () => {
      setIsFetching(true);
      const response = await fetchMessages(orgId, conversation.id, true, messageIdParam);
      setMessages(parseMessages(response.data, response.meta.related));
      setPagination(response.meta.pagination);
      setTimeout(() => inputRef.current?.focus(), 0);
      setTimeout(scrollToBottom, 100);
      setIsFetching(false);

      const { data } = await getAllowedEmojis();
      setAllowedEmojis(data);
    };
    load();
  }, [orgId, conversation.id, setMessages, scrollToBottom, messageIdParam]);

  const updateMessage = useCallback((
    messageId: string, propName: keyof PrivateMessage, value: PrivateMessage[keyof PrivateMessage], conversationId?: string,
  ) => {
    setMessages((prev) => prev?.map((message) => {
      if (message.id === messageId) {
        return { ...message, [propName]: value, is_edited: propName === 'text' };
      }
      return message;
    }) || null);
    if (conversationId) {
      onUpdateLastMessage(conversationId, propName, value, messageId);
    }
  }, [messages, setMessages, onUpdateLastMessage]);

  useEffect(() => {
    if (conversation.last_message?.id && conversation.last_message?.seen === false) {
      updateMessage(conversation.last_message?.id, 'seen', true, conversation.id);
    }
  }, [conversation.last_message, conversation.id, updateMessage]);

  const handleShowMore = useCallback(async () => {
    setIsFetchingMore(true);
    const oldHeight = contentRef.current?.offsetHeight || 0;
    const response = await fetchMessages(orgId, conversation.id, pagination?.next_cursor || true);
    setMessages((prev) => [...parseMessages(response.data, response.meta.related), ...(prev || [])]);
    setPagination(response.meta.pagination);
    if (listRef.current && contentRef.current && oldHeight) {
      listRef.current.scrollTop = listRef.current.scrollTop + contentRef.current.offsetHeight - oldHeight;
    }
    setIsFetchingMore(false);
  }, [pagination?.next_cursor, orgId, conversation.id, setMessages, setIsFetchingMore, setPagination]);

  const handleAddMessage = useCallback(async (message: PostMessageFormData) => {
    try {
      const newMessages = await postMessage(orgId, conversation.id, message);
      const parsedMessage = parseMessages(newMessages, { users: Object.values(users) })[0];
      onNewMessage(conversation.id, parsedMessage);
      setReplyToMessage(undefined);
      scrollToBottom();
      setTimeout(() => inputRef.current?.focus(), 0);
      if (currentConversation?.id === conversation.id &&
        currentConversation?.is_archived &&
        currentFilter === EConversationFilters.ARCHIVED) {
        setCurrentConversation({
          ...currentConversation,
          is_archived: false,
        });
        setConversations((prev) => prev.filter((item) => item.id !== conversation.id));
      }
    } catch (e: any) {
      const failedMessage: PrivateMessage = {
        type: EMessageTypes.FAILED,
        text: message.text,
        created_at: new Date().toISOString(),
        seen: false,
        object_id: '',
        reactions: [],
        // @ts-expect-error
        attachments: [
          ...message.attachments,
          ...(message.gif ? [message.gif] : []),
        ],
        gif: message.gif,
        is_edited: false,
        id: uniqueId(),
        user: loggedUser,
        user_id: loggedUser.id,
      };
      onNewMessage(conversation.id, failedMessage);

      setTimeout(() => {
        scrollToBottom();
        inputRef.current?.focus();
      }, 0);
    }
  }, [
    orgId,
    conversation.id,
    users,
    onNewMessage,
    scrollToBottom,
    setReplyToMessage,
    loggedUser,
    currentConversation,
    currentFilter,
    setConversations,
  ]);

  const handleEditMessage = useCallback(async (message: PrivateMessage) => {
    await putMessage(orgId, conversation.id, message.id, message);
    updateMessage(message.id, 'text', message.text, conversation.id);
  }, [orgId, conversation.id, updateMessage]);

  const handleReplyToMessage = useCallback((messageId?: string) => {
    setReplyToMessage(messages?.find((message) => message.id === messageId));
    inputRef.current?.focus();
  }, [messages, inputRef, setReplyToMessage]);

  const handleRemoveMessage = useCallback(async (messageId: string) => {
    const message = messages?.find((item) => item.id === messageId);
    if (message?.type !== EMessageTypes.FAILED) {
      const response = await deleteMessage(orgId, conversation.id, messageId);
      if (response?.success) updateMessage(messageId, 'deleted_at', new Date().toISOString(), conversation.id);
    } else {
      setMessages((prev) => prev?.filter((item) => item.id !== messageId) || null);
    }
  }, [orgId, conversation.id, updateMessage, messages, setMessages]);

  const handleSetEmojiReaction = useCallback(async (messageId: string, emoji: AllowedEmoji, isSelected: boolean) => {
    try {
      const response = await setMessageEmoji(orgId, conversation.id, messageId, emoji, !isSelected);
      updateMessage(messageId, 'reactions', response.data.reactions);
    } catch (e: any) {
      if (e && typeof e === 'object' && 'status_code' in e) {
        AlertService.forStatus(e.status_code, {
          warning: t('chat:setting_emoji_reaction_warning'),
          error: t('chat:setting_emoji_reaction_error'),
        });
      }
    }
  }, [orgId, conversation.id, updateMessage]);

  const handlePusherNewMessage = useCallback((
    _: string, id?: string, message?: PrivateMessage, markAsSeen: boolean = false,
  ) => {
    // Prevent new message from being added to favorites or groups if the conversation does not exist.
    if (selectedFilter === EConversationFilterTypes.FAVORITES || selectedFilter === EConversationFilterTypes.GROUPS) {
      const conversationExists = conversations?.find((item) => item.id === id);
      if (!conversationExists) return;
    }

    if (!message || !id) return;
    onNewMessage(id, {
      ...message,
      seen: markAsSeen ? true : message.seen,
    });
    setTimeout(scrollToBottom, 0);
  }, [onNewMessage, scrollToBottom, selectedFilter, conversations]);

  const retryMessage = useCallback((message: PrivateMessage) => {
    handleRetryMessage(message);
    scrollToBottom();
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [handleRetryMessage, scrollToBottom]);

  // Socket listener
  usePusherMessagesListener({
    handlePusherNewMessage,
    updateMessage,
    messages,
    users,
    conversation,
  });

  const days = useMemo(() => formatMessages(messages || []), [messages]);

  // Scroll to highlighted message when messages are loaded from message search
  useEffect(() => {
    if (
      !hasScrolledToHighlight.current &&
      messages &&
      messages.length &&
      !isFetching
    ) {
      setTimeout(() => {
        const container = listRef.current;
        if (!container) return;

        let messageEl: HTMLElement | null = null;

        if (messageIdParam) {
          messageEl = container.querySelector(`[data-message-id="${messageIdParam}"]`) as HTMLElement;
        }

        if (messageEl) {
          const previousHighlights = container.querySelectorAll('.Hightlight_Conversation_Message');
          previousHighlights.forEach((el) => el.classList.remove('Hightlight_Conversation_Message'));

          const textContent = messageEl.querySelector('.Linkify span');
          if (textContent) {

            const message = messages.find((m) => m.id === messageIdParam);
            if (message && message.text && search) {
              textContent.innerHTML = message.text.replace(
                new RegExp(search, 'gi'),
                (match) => `<span class="Hightlight_Conversation_Message">${match}</span>`
              );
            }
          }

          const top = messageEl.offsetTop - container.clientHeight / 2 + messageEl.clientHeight / 2;
          container.scrollTo({ top, behavior: 'smooth' });
          hasScrolledToHighlight.current = true;
        }
      }, 500);
    }
  }, [messages, messageIdParam, isFetching, search]);

  useEffect(() => {
    hasScrolledToHighlight.current = false;
    const container = listRef.current;
    if (container) {
      const highlightedMessages = container.querySelectorAll('.Hightlight_Conversation_Message');
      highlightedMessages.forEach((el) => el.classList.remove('Hightlight_Conversation_Message'));
    }
  }, [conversation.id, messageIdParam]);

  const handleGoToConversation = useCallback(() => {
    history.push(`${basePath}/${conversation.id}`);
    setCurrentConversation({
      ...conversation,
      is_message_search: false,
    });
    setIsSearchFocused(false);
  }, [history, basePath, conversation.id, setCurrentConversation, setIsSearchFocused]);

  const noOpMessageHandler = useCallback(() => { }, []);
  const noOpEmojiHandler = useCallback(() => { }, []);
  const noOpEditHandler = useCallback(() => { }, []);

  return (
    <>
      <div
        ref={listRef}
        className="Conversation__Content fs-exclude"
      >
        {isFetching && <Spinner centered size="large" />}
        {!isFetching && messages?.length === 0 && (
          <div className="Conversation__Content__Placeholder">
            <div><Icon type="chat__filled" /></div>
            {t('chat:conversation_messages_placeholder')}
          </div>
        )}
        {!isFetching && (
          <div ref={contentRef}>
            {pagination?.next_cursor && (
              <Button
                isLoading={isFetchingMore}
                className="Conversation__LoadMore"
                onClick={handleShowMore}
                type="white"
              >
                {t('chat:conversation_load_older_messages')}
              </Button>
            )}
            {days.map(({ date, sections }) => (
              <ConversationsByDay
                key={date}
                date={date}
                sections={sections}
                conversation={conversation}
                allowedEmojis={allowedEmojis}
                usersStatus={usersStatus}
                onReply={isSearchMode ? noOpMessageHandler : handleReplyToMessage}
                onRemove={isSearchMode ? noOpMessageHandler : handleRemoveMessage}
                onSetEmojiReaction={isSearchMode ? noOpEmojiHandler : handleSetEmojiReaction}
                onEditParticipants={onEditParticipants}
                onEdit={isSearchMode ? noOpEditHandler : handleEditMessage}
                handleRetryMessage={retryMessage}
              />
            ))}
          </div>
        )}
      </div>
      {showGoToConversation && (
        <div className="tw-flex tw-justify-center tw-px-6 tw-py-2 tw-bg-neutral-100">
          <Button
            onClick={handleGoToConversation}
            type="primary"
            size="fill"
          >
            {t('chat:conversation_go_to_conversation')}
          </Button>
        </div>
      )}

      {conversation.has_left && (
        <div className="PostPrivateMessage PostPrivateMessage--disabled tw-px-6 tw-py-2 tw-text-center tw-text-neutral-500">
          {t('chat:conversation_has_left_message')}
        </div>
      )}

      {!showGoToConversation && !conversation.has_left && (
        <PostMessageForm
          inputRef={inputRef}
          form={`post-message/${conversation.id}`}
          replyTo={replyToMessage}
          handleReplyToMessage={handleReplyToMessage}
          onAddMessage={handleAddMessage}
        />
      )}
    </>
  );
};

export default Messages;
